import React, { useReducer } from 'react';
import { Stack, ControlLink } from '@moda/om';
import { PaymentSource } from '../../../constants';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { useAddressesQuery } from '../../../hooks/useAddress';
import { usePaymentMethodsQuery } from '../../../hooks/usePaymentMethods';
import { Loading } from '../../Loading';
import { Box } from '../../Box';
import { PaymentMethodOption } from '../PaymentMethodOption';
import { PaymentMethodForm } from '../PaymentMethodForm';
import { PaymentMethodDeleteModal } from '../PaymentMethodDeleteModal';
import AccountPage from '../../../pages/AccountPage';

import './AccountPaymentMethods.scss';

const PAGE_TITLE = 'Payment Methods';

enum Mode {
  Resting,
  Creating,
  Editing,
  Deleting
}

type State = {
  mode: Mode;
  isSaving: boolean;
  selectedPaymentMethodId?: string;
};

type Action =
  | { type: 'ADD' }
  | { type: 'EDIT'; id: string }
  | { type: 'DELETE'; id: string }
  | { type: 'CANCEL' }
  | { type: 'START_SAVING' }
  | { type: 'FINISH_SAVING' }
  | { type: 'FAIL_SAVING' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD':
      return { ...state, mode: Mode.Creating };
    case 'EDIT':
      return { ...state, mode: Mode.Editing, selectedPaymentMethodId: action.id };
    case 'DELETE':
      return { ...state, mode: Mode.Deleting, selectedPaymentMethodId: action.id };
    case 'CANCEL':
      return { ...state, mode: Mode.Resting, selectedPaymentMethodId: undefined };
    case 'START_SAVING':
      return { ...state, isSaving: true };
    case 'FINISH_SAVING':
      return { ...state, isSaving: false, mode: Mode.Resting, selectedPaymentMethodId: undefined };
    case 'FAIL_SAVING':
      return { ...state, isSaving: false, mode: Mode.Resting, selectedPaymentMethodId: undefined };
    default:
      return state;
  }
};

export const AccountPaymentMethods: React.FC = () => {
  const { loading: loadingAddresses, error: addressesError } = useAddressesQuery();
  const {
    data: paymentMethods,
    loading: loadingPaymentMethods,
    error: paymentMethodsError
  } = usePaymentMethodsQuery();
  const [state, dispatch] = useReducer(reducer, { mode: Mode.Resting, isSaving: false });

  if (addressesError) throwQueryError(addressesError);
  if (paymentMethodsError) throwQueryError(paymentMethodsError);

  if (loadingAddresses || loadingPaymentMethods)
    return (
      <AccountPage title={PAGE_TITLE}>
        <Loading />
      </AccountPage>
    );

  return (
    <AccountPage
      title={PAGE_TITLE}
      description={
        state.mode === Mode.Resting && (
          <Stack space={0}>
            <ControlLink
              className="AccountPaymentMethods__add-button"
              onClick={() => dispatch({ type: 'ADD' })}
            >
              Add New Payment Method
            </ControlLink>
          </Stack>
        )
      }
    >
      <Stack space={5}>
        {state.mode === Mode.Creating && (
          <Box>
            <PaymentMethodForm
              submitButtonLabel="Add"
              onCancel={() => dispatch({ type: 'CANCEL' })}
              onSave={() => dispatch({ type: 'FINISH_SAVING' })}
            />
          </Box>
        )}

        <Stack space={4}>
          {paymentMethods?.paymentMethods.data
            .filter(paymentMethod => paymentMethod.attributes.source === PaymentSource.Adyen)
            .map(paymentMethod =>
              state.mode === Mode.Editing && state.selectedPaymentMethodId === paymentMethod.id ? (
                <Box key={paymentMethod.id}>
                  <PaymentMethodForm
                    method={paymentMethod}
                    onCancel={() => dispatch({ type: 'CANCEL' })}
                    onSave={() => dispatch({ type: 'FINISH_SAVING' })}
                  />
                </Box>
              ) : (
                <PaymentMethodOption
                  key={paymentMethod.id}
                  method={paymentMethod}
                  onDelete={() => dispatch({ type: 'DELETE', id: paymentMethod.id })}
                  onEdit={() => dispatch({ type: 'EDIT', id: paymentMethod.id })}
                />
              )
            )}
        </Stack>

        {state.mode === Mode.Deleting && state.selectedPaymentMethodId && (
          <PaymentMethodDeleteModal
            paymentMethodId={state.selectedPaymentMethodId}
            onClose={() => dispatch({ type: 'CANCEL' })}
          />
        )}
      </Stack>
    </AccountPage>
  );
};
